/**
 * Targeted selection links: a staff URL can target the user's active selection through query params
 * (`associationId`, `assessmentId`, `ageGroupIds`) or through record ids in its path. These are the
 * pure halves — reading targets out of a URL, removing handled params, judging whether the cache
 * already shows a path target in the selection, and deciding what the gate does.
 * See CLAUDE.md → "Targeted selection links".
 */
import _ from 'lodash';
import { matchPath } from 'react-router-dom';

import { SELECTION_TARGET_ROUTES } from '../../../_configuration';
import {
  IActiveSelection, ICachedSelectionScope, ISelectionTarget, ISelectionTargetInput, SelectionTargetAction,
} from '../types/selection-target.types';

export const SELECTION_TARGET_PARAMS = {
  ASSOCIATION: 'associationId',
  ASSESSMENT: 'assessmentId',
  AGE_GROUPS: 'ageGroupIds',
} as const;

const readParam = (params: URLSearchParams, key: string) => params.get(key)?.trim() || undefined;

// `ageGroupIds` accepts repeated keys (`?ageGroupIds=a&ageGroupIds=b`) and a comma list (`?ageGroupIds=a,b`).
export const readSelectionTarget = (search: string): ISelectionTarget | null => {
  const params = new URLSearchParams(search);
  const associationId = readParam(params, SELECTION_TARGET_PARAMS.ASSOCIATION);
  const assessmentId = readParam(params, SELECTION_TARGET_PARAMS.ASSESSMENT);
  const ageGroupIds = _.uniq(_.compact(
    _.flatMap(params.getAll(SELECTION_TARGET_PARAMS.AGE_GROUPS), (value) => value.split(',')).map((id) => id.trim()),
  ));

  if (!associationId && !assessmentId && !ageGroupIds.length) return null;
  return { associationId, assessmentId, ageGroupIds: ageGroupIds.length ? ageGroupIds : undefined };
};

// Stops at the first route that matches, so each path is matched at most once per route.
export const readRouteSelectionTarget = (pathname: string): ISelectionTarget | null => {
  for (const route of SELECTION_TARGET_ROUTES) {
    const match = matchPath<Record<string, string>>(pathname, { path: route.path, exact: true });
    if (match) {
      return _.transform(route.params, (target: ISelectionTarget, targetKey, param) => {
        Object.assign(target, { [targetKey]: match.params[param] });
      }, {});
    }
  }
  return null;
};

export const stripSelectionTarget = (search: string): string => {
  const params = new URLSearchParams(search);
  Object.values(SELECTION_TARGET_PARAMS).forEach((key) => params.delete(key));
  const remaining = params.toString();
  return remaining ? `?${remaining}` : '';
};

export const toSelectionTargetInput = ({ associationId, ...target }: ISelectionTarget): ISelectionTargetInput => ({
  ...target,
  companyId: associationId,
});

// A location's identity for "already handled": the history key distinguishes two visits to one URL.
export const selectionLocationKey = ({ key, pathname, search }: { key?: string; pathname: string; search: string }) =>
  `${key || ''}|${pathname}${search}`;

// True only when the cached scope proves the record sits in the selection; anything unknown is not current.
export const isCachedScopeCurrent = (scope: ICachedSelectionScope | null, selection?: IActiveSelection): boolean => {
  if (!scope || !selection) return false;
  if (scope.ageGroupId && !(selection.activeAgeGroupIds || []).includes(scope.ageGroupId)) return false;
  if (scope.assessmentId) return scope.assessmentId === selection.activeAssessmentId;
  if (scope.companyId !== undefined) return scope.companyId === null || scope.companyId === selection.activeCompanyId;
  return false;
};

interface ISelectionTargetDecision {
  isSignedIn: boolean;
  isHandled: boolean;
  isOnline: boolean;
  queryTarget: ISelectionTarget | null;
  routeTarget: ISelectionTarget | null;
  isRouteTargetCurrent: boolean;
}

// Query targets always go to the server (or are dropped offline); path targets only when the cache can't vouch for them.
// A path target that needs no request is still settled, so a selector change made on its page is never undone.
export const decideSelectionTargetAction = ({
  isSignedIn, isHandled, isOnline, queryTarget, routeTarget, isRouteTargetCurrent,
}: ISelectionTargetDecision): SelectionTargetAction => {
  if (!isSignedIn || isHandled) return SelectionTargetAction.NONE;
  if (queryTarget) return isOnline ? SelectionTargetAction.APPLY : SelectionTargetAction.SKIP_OFFLINE;
  if (!routeTarget) return SelectionTargetAction.NONE;
  return isOnline && !isRouteTargetCurrent ? SelectionTargetAction.APPLY : SelectionTargetAction.SETTLE;
};
